import React from 'react'
import { useForm, UseFormReturn } from 'react-hook-form'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'

import { Payment } from '../../../types/payment'
import {
  PaymentAmountView,
  PaymentDeliveryView,
  PaymentInformationView,
  PaymentSourceView,
} from './components'
import WidgetBody from '../../../base/Widget/WidgetBody'
import { LockIconLight } from '../../../assets/icons/LockIconLight'
import WidgetSection, {
  WidgetSectionProps,
} from '../../../base/Widget/WidgetSection'
import { useTheme } from '@mui/material/styles'

const defaultSx = {
  link: {
    textDecoration: 'none',
    color: '#1DAEEC',
  },
  policyLabel: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: '11px',
    fontWeight: '325',
    lineHeight: '16px',
  },
  sectionTitle: {
    color: '#5D5D5D',
    fontWeight: '400',
    lineHeight: '16px',
    fontSize: '12px',
  },
  titleLabel: {
    width: '100%',
    marginBottom: '24px',
    textTransform: 'uppercase',
    fontWeight: 600,
  },
}

const ReviewSection = (props: WidgetSectionProps) => {
  const theme = useTheme()
  return (
    <WidgetSection
      sx={{
        paddingBottom: theme.spacing(3),
        ...(props.isLastSection
          ? {}
          : {
              borderBottom: '1px solid #00000033',
            }),
      }}
      {...props}
    >
      {props.children}
    </WidgetSection>
  )
}

export interface PaymentReviewProps {
  attachments?: Array<string>
  form?: UseFormReturn
  onClickBack?: Function
  onClickEdit?: Function
  onSubmit?: Function
  payment: Payment
  title: string
  isStart?: boolean
  config?: {
    editAmount?: boolean
    showDeliveryInformation?: boolean
  }
}

const FinePrint = () => {
  return (
    <Typography
      sx={{
        fontSize: '0.6875rem',
        fontWeight: 325,
        lineHeight: 1.455,
        color: '#00000080',
      }}
    >
      By clicking "Submit Payment" you authorize the debit of the payment method
      as outlined above and agree to the following{' '}
      <Link
        sx={defaultSx.link}
        href='https://www.plastiq.com/guest-terms'
        target='_blank'
        rel='noopener'
      >
        Terms of Service
      </Link>{' '}
      and{' '}
      <Link
        sx={defaultSx.link}
        href='https://www.plastiq.com/privacy'
        target='_blank'
        rel='noopener'
      >
        Privacy Policy
      </Link>
      .
    </Typography>
  )
}

/**
 * @param {PaymentReviewProps} props
 * @param {string} props.title (Optional) - title of the form
 * @param {Function} props.onClickBack (optional) - Callback fired when the user clicks on back button
 * @param {Function} props.onClickEdit (optional) - Callback fired when the user clicks on edit button
 */
const PaymentReview = (props: PaymentReviewProps) => {
  const useFormDefault = props.form || useForm()

  const { showDeliveryInformation = true, editAmount = true } =
    props.config || {}

  const {
    formState: { isSubmitting },
  } = useFormDefault

  const onClickBack = () => {
    props.onClickBack && props.onClickBack()
  }

  const onClickEdit = () => {
    props.onClickEdit && props.onClickEdit()
  }

  const onClickSubmit = () => {
    props.onSubmit && props.onSubmit()
  }

  return (
    <WidgetBody
      title={props.title}
      onBack={onClickBack}
      onContinue={onClickSubmit}
      ctaLabel='Submit Payment'
      ctaIcon={<LockIconLight />}
      isSubmitting={isSubmitting}
    >
      <ReviewSection>
        <PaymentAmountView
          onClickEdit={onClickEdit}
          payment={props.payment}
          editAmount={editAmount}
        />
      </ReviewSection>
      <ReviewSection
        title='Paying with'
        isLastSection={!showDeliveryInformation}
      >
        <PaymentSourceView payment={props.payment} />
      </ReviewSection>
      {showDeliveryInformation ? (
        <>
          <ReviewSection title='Delivery Method'>
            <PaymentDeliveryView payment={props.payment} />
          </ReviewSection>
          <ReviewSection title='Payment Information' isLastSection>
            <PaymentInformationView
              attachments={props.attachments}
              payment={props.payment}
            />
          </ReviewSection>
          <FinePrint />
        </>
      ) : (
        <FinePrint />
      )}
    </WidgetBody>
  )
}

PaymentReview.defaultProps = {
  title: 'Review Your Payment',
  isStart: false,
  editAmount: true,
}

export default PaymentReview
